<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useExtendedI18n } from '@/i18n'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useEntityStore } from '../../stores/better-entity-store'
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { useFundForm } from '../../utils/use-fund-form'
import { useCatalogStore } from '@/modules/billing/stores/catalog-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { currencyOptions } from '@/modules/shared/utils/money'
import { toFormData } from '@/modules/shared/utils/form'
import { VConfirmDialog, VNotification } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useUnsavedChangesGuard } from '@/modules/shared/composables/use-unsaved-changes-guard'
import { useOnboarding, type OnboardingStep } from '@/modules/shared/composables/use-onboarding'
import OnboardingShell from '@/modules/shared/components/onboarding/OnboardingShell.vue'
import VShimmerLogo from '@/modules/connecting/components/v-shimmer-logo.vue'

import FundConfigStep from './components/fund-config-step.vue'
import FundStructureStep from './components/fund-structure-step.vue'
import FundAdminStep from './components/fund-admin-step.vue'
import FundCapitalStep from './components/fund-capital-step.vue'
import FundRaisingStep from './components/fund-raising-step.vue'
import FundBankStep from './components/fund-bank-step.vue'
import FundTaxStep from './components/fund-tax-step.vue'
import FundReviewStep from './components/fund-review-step.vue'

import type { AddOn, Subscription } from '../../types'

// ── Route & Stores ──────────────────────────────────────────────

const route = useRoute()
const router = useRouter()
const { slug } = route.params as { slug: string }
const entityId = computed(() => (route.params as any).entity_id as string | undefined)

const isEditMode = computed(() => route.name === 'investing.fund-edit')

const authStore = useAuthStore()
const entityStore = useEntityStore()
const addressStore = useAddressStore()
const notificationStore = useNotificationStore()
const { n } = useExtendedI18n()
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

// ── Form ────────────────────────────────────────────────────────

const hasBillingPaid = computed(() => {
  if (!entityStore.entityExpanded) return false
  return entityStore.entityExpanded.is_billing_paid
})
const isLegacyBilling = computed(() => {
  if (!entityStore.entityExpanded) return false
  return entityStore.entityExpanded.is_legacy_billing === true
})
const isFundConfigDisabled = computed(() => hasBillingPaid.value || isLegacyBilling.value)

const catalogStore = useCatalogStore()

const {
  adminOptions,
  managerOptions,
  capitalCallFrequencyOptions,
  ownershipTypeOptions,
  selectedBillingFrequencyOptions,
  visibilityOptions,
  investorOptions,
  subscriptionOptions,
  addOnOptions,
  legalStructureOptions,
  initializeFundForm,
  form,
  v$,
  totalDue,
  buildPayload,
  setFormWithEntityData,
  check_bank_account_number,
  resetFormState,
} = useFundForm(isEditMode.value ? 'edit' : 'create', slug)

// ── Plan resolution ─────────────────────────────────────────────
// Resolves the Fund's selected plan from current options, catalog (legacy),
// or stripe name (sales-created product not in the catalog).

const selectedPlan = computed<Subscription | null>(() => {
  const services = entityStore.entityExpanded?.selected_services || form.value.selected_services || []
  const addOnNames = catalogStore.fundAddOns.map((a) => a.stripe_name)
  const planName = services.find((s: string) => !addOnNames.includes(s))
  if (!planName) return null

  const currentMatch = subscriptionOptions.value.find((s) => s.value === planName)
  if (currentMatch) return currentMatch

  const frequency = form.value.selected_billing_frequency as 'month' | 'quarter' | 'year'
  const catalogPlan = catalogStore.findFundPlanByStripeName(planName)
  if (catalogPlan) {
    return {
      title: catalogPlan.title,
      value: catalogPlan.stripe_name,
      price: catalogPlan.prices?.[frequency] || '',
      included: catalogPlan.included || [],
      excluded: catalogPlan.excluded || [],
    }
  }

  return { title: planName, value: planName, price: '', included: [] }
})

// ── Subscription selection ──────────────────────────────────────

const selectedSubscription = ref<Subscription | null>(null)

const selectSubscription = (subscription: Subscription) => {
  if (selectedSubscription.value) {
    form.value.selected_services = form.value.selected_services.filter(
      (p: string) => p !== selectedSubscription.value!.value,
    )
  }
  selectedSubscription.value = subscription
  if (!form.value.selected_services.includes(subscription.value)) {
    form.value.selected_services.push(subscription.value)
  }
}

const selectAddOn = (addOn: AddOn) => {
  const planExists = form.value.selected_services.includes(addOn.value)
  if (planExists) {
    form.value.selected_services = form.value.selected_services.filter((p: string) => p !== addOn.value)
  } else {
    form.value.selected_services.push(addOn.value)
  }
}

const formattedTotalDue = computed(() => n(totalDue.value, 'currency'))

// ── Watchers (from original Form.vue) ───────────────────────────

watch(
  () => form.value.selected_billing_frequency,
  () => {
    if (selectedSubscription.value) {
      const newSub = subscriptionOptions.value.find((sub) => sub.value === selectedSubscription.value?.value)
      if (newSub) selectSubscription(newSub)
    }
  },
  { immediate: true },
)

watch(
  () => form.value.is_venture360_the_administrator,
  (val) => {
    form.value.is_filing_required = val
    form.value.selected_services = []
    selectedSubscription.value = null
    if (!val) {
      form.value.is_venture360_forming_this_fund = false
    }
  },
)

// ── Raising ─────────────────────────────────────────────────────

const raisingStatus = computed({
  get: () => form.value.raising_status,
  set: (val: string) => {
    form.value.raising_status = val
  },
})
const supportingDocuments = ref<File[]>([])
const existingFiles = ref<string[]>([])

// ── Wizard Steps ────────────────────────────────────────────────

const ALL_STEPS: OnboardingStep[] = [
  {
    key: 'config',
    icon: 'Settings',
    title: 'Fund configuration',
    subtitle: 'Answer a few questions to determine the best structure for your fund.',
  },
  {
    key: 'structure',
    icon: 'Building',
    title: 'General structure',
    subtitle: 'Define the legal entity and address for your fund.',
  },
  {
    key: 'raising',
    icon: 'TrendingUp',
    title: 'Raising',
    subtitle: 'Tell us about your fundraise and fund details.',
  },
  {
    key: 'admin',
    icon: 'Users',
    title: 'Administration',
    subtitle: 'Assign key roles for fund administration.',
  },
  {
    key: 'capital',
    icon: 'TrendingUp',
    title: 'Structure',
    subtitle: 'Define the economic terms and structure of your fund.',
  },
  {
    key: 'bank',
    icon: 'Bank',
    title: 'Bank details',
    subtitle: 'Bank information will be populated in capital calls.',
  },
  {
    key: 'tax',
    icon: 'FileLines',
    title: 'Tax details',
    subtitle: 'Configure the tax filing details for this entity.',
  },
  {
    key: 'review',
    icon: 'CheckCircle',
    title: 'Review and confirm',
    subtitle: 'Review your fund details before finalizing.',
  },
]

const STEPS = ALL_STEPS.filter((s) => s.key !== 'tax' || authStore.is_site_admin)

const currentStepKey = computed(() => STEPS[onboarding.currentStep.value]?.key)

// ── Step refs ───────────────────────────────────────────────────

const configStep = ref<InstanceType<typeof FundConfigStep> | null>(null)
const structureStep = ref<InstanceType<typeof FundStructureStep> | null>(null)
const adminStep = ref<InstanceType<typeof FundAdminStep> | null>(null)
const capitalStep = ref<InstanceType<typeof FundCapitalStep> | null>(null)
const raisingStep = ref<InstanceType<typeof FundRaisingStep> | null>(null)
const bankStep = ref<InstanceType<typeof FundBankStep> | null>(null)
const taxStep = ref<InstanceType<typeof FundTaxStep> | null>(null)

const validateStep = async (stepIndex: number): Promise<boolean> => {
  const key = STEPS[stepIndex]?.key
  switch (key) {
    case 'config':
      return (await configStep.value?.isValid()) ?? true
    case 'structure':
      return (await structureStep.value?.isValid()) ?? true
    case 'admin':
      return (await adminStep.value?.isValid()) ?? true
    case 'capital':
      return (await capitalStep.value?.isValid()) ?? true
    case 'raising':
      return (await raisingStep.value?.isValid()) ?? true
    case 'bank':
      return (await bankStep.value?.isValid()) ?? true
    case 'tax':
      return (await taxStep.value?.isValid()) ?? true
    default:
      return true
  }
}

const onboarding = useOnboarding({
  steps: STEPS,
  startAtReview: isEditMode.value,
  scrollSelector: '.onboarding-scroll',
  validateStep,
})

// ── Submit ──────────────────────────────────────────────────────

const submitError = ref<string | null>(null)
const submitting = ref(false)

const afterCreate = (newEntityId: string, hasSubscription: boolean) => {
  if (!newEntityId) return

  if (hasSubscription) {
    router.replace({ name: 'billing-entity', params: { billable_id: newEntityId, billable_type: 'fund' } })
    return
  }

  router.replace({ name: 'investing.entity-overview', params: { entity_id: newEntityId, entity_type: 'fund' } })
}

const onSubmit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  submitError.value = null
  submitting.value = true

  try {
    const payload = buildPayload()
    if (isEditMode.value && entityId.value) {
      payload.id = entityId.value
    }

    if (supportingDocuments.value.length > 0 && payload.deal_card_attributes) {
      payload.deal_card_attributes.supporting_documents = supportingDocuments.value
    }

    const hasFiles = supportingDocuments.value.length > 0
    const body = hasFiles ? toFormData({ fund: payload }) : { fund: payload }
    const data: any = await useFetch(`/v4/${slug}/investing/investor-sets/setup-fund`).post(body)

    leaveGuard.allowLeave()

    if (isEditMode.value) {
      router.replace({
        name: 'investing.entity-overview',
        params: { entity_id: entityId.value, entity_type: 'fund' },
      })
    } else {
      afterCreate(data?.id, payload.selected_services.length > 0)
    }
  } catch (error: any) {
    submitError.value = error?.message || 'An unexpected error occurred. Please try again.'
    notificationStore.enqueue('error', submitError.value!)
  } finally {
    submitting.value = false
  }
}

const leaveGuard = useUnsavedChangesGuard({ enabled: () => !isEditMode.value })

// ── Delete (edit mode only) ─────────────────────────────────────

const deleteModal = useModal()
const deleting = ref(false)

const handleConfirmDelete = async () => {
  if (deleting.value || !entityId.value) return
  deleting.value = true
  try {
    await entityStore.deleteEntity(entityId.value, { slug })
    deleteModal.close()
    router.replace({ name: 'investing.entities', params: { slug, entity_type: 'funds' } })
  } catch (error: any) {
    notificationStore.enqueue('error', error?.message || 'Failed to delete fund. Please try again.')
  } finally {
    deleting.value = false
  }
}

// ── Mount ───────────────────────────────────────────────────────

onMounted(async () => {
  showSkeleton()
  resetFormState()
  entityStore.reset()

  await Promise.all([initializeFundForm(), addressStore.fetchCountries(), addressStore.fetchStates('US')])

  if (isEditMode.value && entityId.value) {
    await entityStore.fetchEntity(entityId.value, { slug })
    setFormWithEntityData(entityStore.entityExpanded)

    // Fetch existing supporting documents from the deal card folder
    const dealCard = entityStore.entityExpanded?.deal_card
    if (dealCard?.filing_folder_id) {
      const files: any[] = await useFetch(`/v4/${slug}/filing/files?folder_id=${dealCard.filing_folder_id}`).get()
      existingFiles.value = (files || []).map((f: any) => f.name)
    }

    // Restore selected subscription from entity data
    const selectedSub = subscriptionOptions.value.find((s) =>
      form.value.selected_services.some((service: string) => s.value === service),
    )
    if (selectedSub) selectSubscription(selectedSub)
  } else {
    // Default to the first (Standard) subscription for new funds
    const defaultSub = subscriptionOptions.value[0]
    if (defaultSub) selectSubscription(defaultSub)
  }

  watch(
    () => entityStore.entityExpanded,
    (newEntity) => {
      if (!newEntity || !isEditMode.value) return
      setFormWithEntityData(newEntity)

      const selectedSub = subscriptionOptions.value.find((s) =>
        form.value.selected_services.some((service: string) => s.value === service),
      )
      if (selectedSub) selectSubscription(selectedSub)
    },
  )

  hideSkeleton()
})
</script>

<template>
  <OnboardingShell
    :steps="STEPS"
    :current-step="onboarding.currentStep.value"
    :highest-step-reached="onboarding.highestStepReached.value"
    :is-first-step="onboarding.isFirstStep.value"
    :is-last-step="onboarding.isLastStep.value"
    :step-menu-open="onboarding.stepMenuOpen.value"
    :loading="skeleton"
    :submitting="submitting"
    :submit-label="isEditMode ? 'Done' : 'Create Fund'"
    @update:step-menu-open="onboarding.stepMenuOpen.value = $event"
    @continue="onboarding.onClickContinue"
    @back="onboarding.onClickBack"
    @submit="onSubmit"
    @step-click="onboarding.onStepClick"
  >
    <template #notification>
      <VNotification />
    </template>

    <template #loading>
      <VShimmerLogo />
    </template>

    <FundConfigStep
      v-if="currentStepKey === 'config'"
      ref="configStep"
      :form="form"
      :v$="v$"
      :subscription-options="subscriptionOptions"
      :add-on-options="addOnOptions"
      :selected-billing-frequency-options="selectedBillingFrequencyOptions"
      :is-fund-config-disabled="isFundConfigDisabled"
      :selected-subscription="selectedSubscription"
      :selected-plan="selectedPlan"
      :total-due="formattedTotalDue"
      :initial-value="entityStore.entityExpanded"
      @select-subscription="selectSubscription"
      @select-add-on="selectAddOn"
    />

    <FundStructureStep
      v-if="currentStepKey === 'structure'"
      ref="structureStep"
      :form="form"
      :v$="v$"
      :legal-structure-options="legalStructureOptions"
      :currency-options="currencyOptions"
    />

    <FundAdminStep
      v-if="currentStepKey === 'admin'"
      ref="adminStep"
      :form="form"
      :v$="v$"
      :admin-options="adminOptions"
      :is-new-record="!isEditMode"
    />

    <FundCapitalStep
      v-if="currentStepKey === 'capital'"
      ref="capitalStep"
      :form="form"
      :v$="v$"
      :capital-call-frequency-options="capitalCallFrequencyOptions"
      :ownership-type-options="ownershipTypeOptions"
    />

    <FundRaisingStep
      v-if="currentStepKey === 'raising'"
      ref="raisingStep"
      :form="form"
      :v$="v$"
      :raising-status="raisingStatus"
      :visibility-options="visibilityOptions"
      :investor-options="investorOptions"
      :files="supportingDocuments"
      :existing-files="existingFiles"
      @update:raising-status="raisingStatus = $event"
      @update:files="supportingDocuments = $event"
    />

    <FundBankStep
      v-if="currentStepKey === 'bank'"
      ref="bankStep"
      :form="form"
      :v$="v$"
      :is-new-record="!isEditMode"
      :check-bank-account-number="check_bank_account_number"
    />

    <FundTaxStep v-if="currentStepKey === 'tax'" ref="taxStep" :form="form" :v$="v$" />

    <FundReviewStep
      v-if="currentStepKey === 'review'"
      :form="form"
      :steps="STEPS"
      :subscription-options="subscriptionOptions"
      :add-on-options="addOnOptions"
      :selected-plan="selectedPlan"
      :capital-call-frequency-options="capitalCallFrequencyOptions"
      :ownership-type-options="ownershipTypeOptions"
      :admin-options="adminOptions"
      :total-due="formattedTotalDue"
      :is-new-record="!isEditMode"
      :raising-status="raisingStatus"
      :files="supportingDocuments"
      :existing-files="existingFiles"
      :is-edit-mode="isEditMode"
      @navigate="onboarding.navigateFromReview"
      @delete="deleteModal.open()"
    />
  </OnboardingShell>

  <VConfirmDialog
    :modalStore="deleteModal"
    title="Delete Fund"
    message="Deleting this fund will permanently remove all associated data including commitments, capital calls, distributions, and documents. This action cannot be undone."
    confirmLabel="Delete Fund"
    variant="danger"
    :loading="deleting"
    @confirm="handleConfirmDelete"
  />

  <VConfirmDialog
    :modalStore="leaveGuard.modal"
    title="Discard fund setup?"
    message="If you leave this page, all the information you've entered will be lost. Are you sure you want to discard your progress?"
    confirmLabel="Discard"
    variant="danger"
    @confirm="leaveGuard.confirm"
    @cancel="leaveGuard.cancel"
  />
</template>
